import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Sparkles, Phone } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { profileRepo } from '@/lib/repositories'
import {
  validateUsername,
  getAvatarColor,
  getInitials,
} from '@/lib/utils/avatarUtils'
import { toE164 } from '@/lib/utils/phoneUtils'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'

interface OnboardingSheetProps {
  open: boolean
  onClose: () => void
  username: string
}

export function OnboardingSheet({
  open,
  onClose,
  username: initialUsername,
}: OnboardingSheetProps) {
  const { t } = useTranslation()
  const [username, setUsername] = useState(initialUsername)
  const [displayName, setDisplayName] = useState('')
  const [bio, setBio] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [usernameError, setUsernameError] = useState('')

  // Get avatar styling
  const avatarColor = getAvatarColor(username || initialUsername)
  const initials = getInitials(displayName, username || initialUsername)

  const handleUsernameChange = (value: string) => {
    // Sanitize: only allow letters, numbers, underscores
    const sanitized = value.replace(/[^a-zA-Z0-9_]/g, '')
    setUsername(sanitized)
    // Clear error when user types
    if (usernameError) setUsernameError('')
  }

  const handleSkip = async () => {
    try {
      await profileRepo.completeOnboarding(username || initialUsername, {
        username: username || initialUsername,
      })
      analytics.capture(AnalyticsEvents.ONBOARDING_SKIPPED)
      onClose()
      toast(t('onboardingSkipped'), {
        description: t('updateProfileLater'),
      })
    } catch (error) {
      console.error('Failed to skip onboarding:', error)
    }
  }

  const handleContinue = async () => {
    // Validate username
    const validation = validateUsername(username)
    if (!validation.valid) {
      setUsernameError(validation.error || 'Invalid username')
      return
    }

    try {
      // Save profile via repository
      const formattedPhone = phoneNumber.replace(/\D/g, '').length >= 10
        ? toE164(phoneNumber)
        : undefined

      await profileRepo.completeOnboarding(username, {
        username: username.trim(),
        displayName: displayName.trim() || username.trim(),
        bio: bio.trim(),
        phoneNumber: formattedPhone,
      })

      const finalDisplayName = displayName.trim() || username.trim()

      analytics.capture(AnalyticsEvents.ONBOARDING_COMPLETED, {
        has_display_name: !!displayName.trim(),
        has_bio: !!bio.trim(),
        has_phone: !!formattedPhone,
      })

      toast.success(t('profileUpdatedSuccessfully'), {
        description: t('welcomeToSovereignUser', { name: finalDisplayName }),
      })

      onClose()
    } catch (error) {
      console.error('Failed to save profile:', error)
      toast.error(t('failedToSaveProfile'), {
        description: t('pleaseTryAgain'),
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="bg-neutral-950 border-white/[0.06] flex flex-col h-full max-h-[90vh]">
        {/* Header with Avatar */}
        <SheetHeader className="flex-shrink-0 text-center space-y-4 pb-6 border-b border-white/[0.06]">
          <div
            className={`w-20 h-20 rounded-full bg-gradient-to-br ${avatarColor} mx-auto flex items-center justify-center shadow-lg shadow-emerald-500/10`}
          >
            <span className="text-black text-2xl font-bold">{initials}</span>
          </div>
          <div>
            <SheetTitle className="text-2xl font-bold text-white flex items-center justify-center gap-2">
              {t('welcomeToSovereignExcl')}{' '}
              <Sparkles className="w-6 h-6 text-emerald-400" />
            </SheetTitle>
            <SheetDescription className="text-neutral-400 mt-2">
              {t('personalizeProfile')}
            </SheetDescription>
          </div>
        </SheetHeader>

        {/* Scrollable Form */}
        <div className="flex-1 overflow-y-auto py-6 px-6 space-y-5">
          {/* Username Field */}
          <div className="space-y-2">
            <Label htmlFor="username" className="text-neutral-300">
              {t('username')} <span className="text-red-400">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 z-10 font-mono">
                @
              </span>
              <Input
                id="username"
                value={username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                className={`bg-neutral-900/80 border-white/[0.08] text-neutral-100 placeholder:text-neutral-500 pl-8 font-mono ${
                  usernameError
                    ? 'border-red-500 focus-visible:ring-red-500'
                    : 'focus:border-emerald-500/50 focus:ring-emerald-500/20'
                }`}
                maxLength={20}
                placeholder="your_username"
              />
            </div>
            {usernameError ? (
              <p className="text-xs text-red-400">{usernameError}</p>
            ) : (
              <p className="text-xs text-neutral-500">
                {t('usernameHelp')}
              </p>
            )}
          </div>

          {/* Display Name Field */}
          <div className="space-y-2">
            <Label htmlFor="displayName" className="text-neutral-300">
              {t('displayName')}{' '}
              <span className="text-neutral-500 text-xs font-normal">
                {t('optional')}
              </span>
            </Label>
            <Input
              id="displayName"
              placeholder={t('yourName')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="bg-neutral-900/80 border-white/[0.08] text-neutral-100 placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
              maxLength={30}
            />
            <p className="text-xs text-neutral-500">
              {t('displayNameHelp')}
            </p>
          </div>

          {/* Bio Field */}
          <div className="space-y-2">
            <Label htmlFor="bio" className="text-neutral-300">
              {t('bio')}{' '}
              <span className="text-neutral-500 text-xs font-normal">
                {t('optional')}
              </span>
            </Label>
            <Textarea
              id="bio"
              placeholder={t('bioPlaceholder')}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="bg-neutral-900/80 border-white/[0.08] text-neutral-100 placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
              maxLength={150}
            />
            <p className="text-xs text-neutral-500 text-right font-mono">
              {t('charactersCount', { count: bio.length, max: 150 })}
            </p>
          </div>

          {/* Phone Number Field */}
          <div className="space-y-2">
            <Label htmlFor="phoneNumber" className="text-neutral-300 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              {t('phoneNumberLabel')}{' '}
              <span className="text-neutral-500 text-xs font-normal">
                {t('recommended')}
              </span>
            </Label>
            <Input
              id="phoneNumber"
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="bg-neutral-900/80 border-white/[0.08] text-neutral-100 placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
              maxLength={20}
            />
            <p className="text-xs text-neutral-500">
              {t('phoneOnboardingHelp')}
            </p>
          </div>

          {/* Tip Box */}
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-4">
            <p className="text-sm text-emerald-400">
              💡 <strong>{t('tip')}</strong> {t('tipMessage')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <SheetFooter className="flex-shrink-0 flex-col gap-2 pt-4 border-t border-white/[0.06]">
          <Button
            onClick={handleContinue}
            className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-semibold shadow-lg shadow-emerald-500/25"
          >
            {t('continueBtn')}
          </Button>
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="w-full text-neutral-400 hover:text-white hover:bg-white/[0.06]"
          >
            {t('skipForNow')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
